<template>
  <div class="mechanic-auto">
    <div v-if="propertiesLoading" class="mechanic-auto__loader">
      <v-progress-circular size="50" width="4" color="#464646" indeterminate />
    </div>

    <v-form ref="auto-form" v-else @submit.prevent="onSubmit">
      <auto-media-step
        :media.sync="formData.media"
        :comments.sync="formData.comments"
        :additional-photo.sync="formData.additionalPhoto"
        :additional-video.sync="formData.additionalVideo"
        :additional-photo-comment.sync="formData.additionalPhotoComment"
        :additional-video-comment.sync="formData.additionalVideoComment"
      />

      <auto-check-list-step
        :title="$t('auto.labels.complectation')"
        :prev-state="equipmentLastState"
        :form-data.sync="formData.equipment"
        :params="autosState.equipment"
        :count-list="autosState.equipment_count"
      />

      {{ checkListCommentsLastState }}

      <auto-check-list-step
        :title="$t('auto.labels.technicalCondition')"
        :prev-state="checkListLastState"
        :prev-state-comments="checkListCommentsLastState"
        :form-data.sync="formData.technicalCondition"
        :comments.sync="formData.technicalConditionComemnts"
        :params="autosState.checkList"
        :options="checkListOptions"
        comment-param="0"
      />

      <mechanic-debt-step
        :driver-debt.sync="formData.driver_debt"
        :driver-debt-comment.sync="formData.driver_debt_comment"
      />

      <v-btn
        class="d-block ml-auto"
        type="submit"
        color="warning"
        elevation="0"
        :loading="saving"
        large
      >
        {{ $t('common.btns.save') }}
      </v-btn>
    </v-form>

    <v-snackbar v-model="snack.opened" :color="snack.color" bottom right>
      {{ snack.message }}
    </v-snackbar>
  </div>
</template>

<script>
import AutoMediaStep from './AutoMediaStep.vue';
import AutoCheckListStep from './AutoCheckListStep.vue';
import MechanicDebtStep from './MechanicDebtStep.vue';

export default {
  components: { AutoMediaStep, AutoCheckListStep, MechanicDebtStep },
  data: () => ({
    propertiesLoading: false,
    saving: false,
    tab: 0,
    formData: {
      media: {},
      comments: {},
      equipment: {},
      technicalCondition: {},
      technicalConditionComemnts: {},
      additionalPhoto: [],
      additionalVideo: [],
      additionalPhotoComment: '',
      additionalVideoComment: '',
      driver_debt: '',
      driver_debt_comment: '',
    },
    snack: {
      opened: false,
      color: '',
      message: '',
    },
  }),
  computed: {
    autosState() {
      return this.$store.state.auto.autosState;
    },
    checkListOptions() {
      return [
        {
          id: null,
          title: this.$t('common.absent'),
        },
        {
          id: '0',
          title: this.$t('common.faulty'),
        },
        {
          id: '1',
          title: this.$t('common.serviceable'),
        },
      ];
    },
    equipmentLastState() {
      const { equipment } = this.$store.state.mechanic.autoLastState;

      return Object.keys(equipment).reduce((obj, key) => {
        obj[key] = equipment[key].value; // eslint-disable-line

        return obj;
      }, {});
    },
    checkListLastState() {
      const checkListParams = this.$store.state.auto.autosState.checkList;
      const { checkList } = this.$store.state.mechanic.autoLastState;

      return Object.keys(checkListParams).reduce((obj, key) => {
        obj[key] = checkList[key] ? checkList[key].value : null; // eslint-disable-line

        return obj;
      }, {});
    },
    checkListCommentsLastState() {
      const { checkListComment } = this.$store.state.mechanic.autoLastState;
      return Object.keys(checkListComment).reduce((obj, key) => {
        obj[key] = checkListComment[key].value; // eslint-disable-line
        return obj;
      }, {});
    },
  },
  methods: {
    fillMedia() {
      Object.keys(this.autosState.media).forEach((key) => {
        this.formData.media[key] = null;
      });
    },
    fillEquipment() {
      const { equipment } = this.autosState;

      const data = Object.keys(equipment).reduce((params, key) => {
        params[key] = '0'; // eslint-disable-line
        return params;
      }, {});

      this.formData.equipment = data;
    },
    fillCheckList() {
      const { checkList } = this.autosState;

      const data = Object.keys(checkList).reduce((params, key) => {
        params[key] = null; // eslint-disable-line
        return params;
      }, {});

      this.formData.technicalCondition = data;
    },
    async fetchProperties() {
      this.propertiesLoading = true;

      await Promise.all([
        this.$store.dispatch('auto/fetchAutosProperties'),
        this.$store.dispatch(
          'mechanic/fetchLastState',
          this.$route.params.carId,
        ),
      ]);

      this.propertiesLoading = false;
    },
    getRequestData() {
      const photo = Object.entries(this.formData.media).reduce(
        (obj, [key, value]) => {
          obj[key] = value.name; // eslint-disable-line
          return obj;
        },
        {},
      );

      const additionalPhoto = this.formData.additionalPhoto.map(
        ({ name }) => name,
      );
      const additionalVideo = this.formData.additionalVideo.map(
        ({ name }) => name,
      );

      return {
        driver_debt: this.formData.driver_debt,
        driver_debt_comment: this.formData.driver_debt_comment,
        workDeskItemId: this.$route.query.workDeskItemId,
        state: {
          photo,
          additionalPhoto,
          additionalVideo,
          mediaComment: this.formData.comments,
          equipment: this.formData.equipment,
          checkList: this.formData.technicalCondition,
          checkListComment: this.formData.technicalConditionComemnts,
          additionalPhotoComment: {
            comment: this.formData.additionalPhotoComment,
          },
          additionalVideoComment: {
            comment: this.formData.additionalVideoComment,
          },
        },
      };
    },
    async onSubmit() {
      const isValid = this.$refs['auto-form'].validate();
      const allPhotosComplete = Object.values(
        this.formData.media,
      ).every((item) => Boolean(item));

      if (!isValid) {
        this.snack.message = this.$t('mechanic.mediaValidationError');
        this.snack.color = 'error';
        this.snack.opened = true;
        return;
      }

      if (!allPhotosComplete) {
        this.snack.message = this.$t('mechanic.mediaValidationError');
        this.snack.color = 'error';
        this.snack.opened = true;
        return;
      }

      this.saving = true;

      const { success } = await this.$store.dispatch('mechanic/acceptCar', {
        carId: this.$route.params.carId,
        params: this.getRequestData(),
      });

      this.saving = false;

      if (success) {
        this.snack.message = this.$t('common.saved');
        this.snack.color = 'success';
        this.snack.opened = true;
        this.$router.push('/mechanic/rents');
      } else {
        this.snack.message = this.$t('common.errors.APIerror');
        this.snack.color = 'error';
        this.snack.opened = true;
      }
    },
  },
  async created() {
    await this.fetchProperties();

    this.fillMedia();
    this.fillCheckList();
    this.fillEquipment();
  },
};
</script>

<style lang="scss" scoped>
.mechanic-auto {
  padding: 15px;
  width: 100%;
  margin-top: 25px;
  border-radius: 5px;
  background-color: #fff;

  &__loader {
    display: flex;
    justify-content: center;
    width: 100%;
    padding: 20px;
  }
}
</style>
